import React, { useCallback, useEffect, useState } from 'react';
import { listProspectInvites, resendProspectInvite, cancelCart } from '../api';
import { relativeAge } from '../lib/requests';
import { statusMeta, inviteActions, canCancel, boardInvites, cartSummary, linkFor, sendOutcome } from '../lib/prospectInvites';

// Prospect invites — every cart a rep sent from + Prospect Cart, and where
// each one stands: invite out, link lapsed, account set up, paid. Before this
// a prospect cart was saved and then could not be found again from anywhere
// in the CRM. Compact cards, coloured by status (App.css .inv-*), resend,
// copy and cancel on the card itself. Rules in src/lib/prospectInvites.js.
export default function ProspectInviteBoard({ refreshKey = 0 }) {
  const [invites, setInvites] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);      // cartId while a resend/copy/cancel runs
  const [notice, setNotice] = useState(null);  // { cartId, ok, text }
  const [confirming, setConfirming] = useState(null); // cartId awaiting "Yes, cancel"

  const load = useCallback(() => {
    listProspectInvites({ limit: 30 })
      .then(r => { setInvites(boardInvites(r.invites)); setError(''); })
      .catch(e => {
        // No cart authority: the board is not theirs to see. Say nothing.
        if (e.status === 403) { setInvites([]); return; }
        setError(e.message);
      });
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  async function act(invite, { sendEmail }) {
    setBusy(invite.cartId);
    setNotice(null);
    try {
      const r = await resendProspectInvite(invite.cartId, { sendEmail });
      if (sendEmail) {
        const o = sendOutcome(r, invite.email);
        setNotice({ cartId: invite.cartId, ok: o.ok, text: o.headline });
      } else {
        const url = linkFor(r);
        let copied = false;
        try { await navigator.clipboard.writeText(url); copied = true; } catch { /* shown below instead */ }
        setNotice({ cartId: invite.cartId, ok: true, text: copied ? 'New link copied. The old one no longer works.' : url });
      }
      load();
    } catch (e) {
      setNotice({ cartId: invite.cartId, ok: false, text: e.message });
    }
    setBusy(null);
  }

  // The same cancel as a customer's Carts tab (DELETE /api/carts/:id):
  // squish-backend refuses a paid cart and withdraws the cart's PaymentIntent,
  // so a payment already in the customer's hands cannot land on a dead cart.
  async function cancel(invite) {
    setBusy(invite.cartId);
    setNotice(null);
    try {
      await cancelCart(invite.cartId);
      setConfirming(null);
      load();   // a cancelled cart leaves the board (boardInvites)
    } catch (e) {
      setNotice({ cartId: invite.cartId, ok: false, text: e.message });
    }
    setBusy(null);
  }

  if (invites === null && !error) return null;
  if (invites && invites.length === 0 && !error) return null;
  const open = (invites || []).filter(i => statusMeta(i.status).open).length;

  return (
    <div className="table-card">
      <div className="table-header">
        <span className="table-title">Prospect invites</span>
        <span className="table-count">{open} open</span>
      </div>
      {error && <div className="error-state" style={{ margin: 12 }}>⚠ {error}</div>}
      <ul className="inv-board">
        {(invites || []).map(inv => {
          const meta = statusMeta(inv.status);
          const actions = inviteActions(inv.status);
          const note = notice?.cartId === inv.cartId ? notice : null;
          const isBusy = busy === inv.cartId;
          return (
            <li key={inv.cartId} className={`inv-card ${meta.tone}`}>
              <div className="inv-card-top">
                <span className="inv-pill">{meta.label}</span>
                <span className="inv-card-age" title={inv.lastSentAt ? new Date(inv.lastSentAt).toLocaleString() : ''}>
                  {relativeAge(inv.lastSentAt || inv.sentAt)}
                </span>
              </div>
              <div className="inv-card-title">{inv.name || inv.email}</div>
              <div className="inv-card-meta">
                {inv.name ? inv.email : null}{inv.name && inv.company ? ' · ' : ''}{inv.company || ''}
              </div>
              <div className="inv-card-foot">
                <span className="inv-card-sum">{cartSummary(inv)}{inv.sendCount > 1 ? ` · sent ${inv.sendCount}×` : ''}</span>
                {confirming === inv.cartId ? (
                  <span className="inv-card-confirm" data-write>
                    Cancel this cart? They won't be able to pay for it.
                    <button className="inv-btn" disabled={isBusy} onClick={() => setConfirming(null)}>Keep</button>
                    <button className="inv-btn is-danger is-primary" disabled={isBusy} onClick={() => cancel(inv)}>
                      {isBusy ? '…' : 'Yes, cancel'}
                    </button>
                  </span>
                ) : (actions.resend || canCancel(inv.status)) && (
                  <span className="inv-card-actions" data-write>
                    {actions.resend && (
                      <>
                        <button className="inv-btn" disabled={isBusy} onClick={() => act(inv, { sendEmail: false })} title="Mints a fresh link; the previous one stops working">
                          {actions.copy}
                        </button>
                        <button className="inv-btn is-primary" disabled={isBusy} onClick={() => act(inv, { sendEmail: true })}>
                          {isBusy ? '…' : actions.resend}
                        </button>
                      </>
                    )}
                    {canCancel(inv.status) && (
                      <button className="inv-btn is-danger" disabled={isBusy} onClick={() => { setNotice(null); setConfirming(inv.cartId); }}>
                        Cancel cart
                      </button>
                    )}
                  </span>
                )}
              </div>
              {note && <div className={`inv-card-note ${note.ok ? '' : 'is-bad'}`}>{note.text}</div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
